import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Phone, Camera, Star, Zap, Clock, BadgeCheck, BadgeDollarSign } from "lucide-react";
import { MaskedLine } from "./Reveal";

const BASE_UP = "/hero/base2-up.webp";
const BASE_WINK = "/hero/base2-wink.webp";
const UNIT_SPRITE = "/hero/unit-sprite.webp";

function HeroScene() {
  const [run, setRun] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const start = () => {
      if (cancelled) return;
      requestAnimationFrame(() => requestAnimationFrame(() => !cancelled && setRun(true)));
    };
    Promise.all(
      [BASE_WINK, BASE_UP, UNIT_SPRITE].map((src) => {
        const img = new Image();
        img.src = src;
        return img.decode ? img.decode().catch(() => {}) : Promise.resolve();
      })
    ).then(start);
    const fallback = setTimeout(start, 3000);
    return () => { cancelled = true; clearTimeout(fallback); };
  }, []);

  return (
    <div className="relative w-full" style={{ aspectRatio: "2000 / 1125" }} data-testid="hero-scene">
      <img src={BASE_WINK} alt="Perth hot water tradie tossing a hot water unit" fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-contain" data-testid="hero-base-image" />
      <img src={BASE_UP} alt="" aria-hidden="true" decoding="async" className={`absolute inset-0 w-full h-full object-contain ${run ? "hero-catch" : "opacity-0"}`} data-testid="hero-catch-frame" />
      <img src={UNIT_SPRITE} alt="" aria-hidden="true" decoding="async" className={`absolute ${run ? "hero-toss" : ""}`} style={{ left: "49.6%", bottom: "45%", width: "11%" }} data-testid="hero-toss-unit" />
    </div>
  );
}

export default function Hero({ onFindUnit, onBook, suburb }) {
  return (
    <section id="top" className="relative bg-tpeco-blue text-white overflow-hidden" data-testid="hero-section">
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-[5fr_6fr] gap-4 lg:gap-8 items-center pt-8 lg:pt-0 max-lg:min-h-[calc(100svh-9rem)] lg:min-h-[calc(100svh-5rem)]">
        <div className="relative z-10 lg:py-10">
          <MaskedLine delay={0.05}>
            <span className="chip-brutal !border-white bg-white/15 text-white backdrop-blur-sm mb-4" data-testid="hero-kicker">
              <Zap className="w-4 h-4" strokeWidth={2.5} /> {suburb ? `${suburb.name.toUpperCase()}'S` : "PERTH'S"} HOT WATER SWAP-OUT CREW
            </span>
          </MaskedLine>
          <h1 className="font-display uppercase leading-[0.95] tracking-tight text-6xl sm:text-7xl lg:text-[4.2rem] xl:text-[5rem] drop-shadow-[0_4px_0_rgba(10,10,10,0.25)]" data-testid="hero-headline">
            {suburb ? (
              <>
                <MaskedLine delay={0.15}>HOT WATER</MaskedLine>
                <MaskedLine delay={0.28}>
                  <span className="bg-white text-tpeco-blue px-3 rounded-2xl inline-block">SYSTEMS</span>
                </MaskedLine>
                <MaskedLine delay={0.41}>
                  <span className="bg-tpeco-pink text-white px-3 rounded-2xl inline-block">{suburb.name.toUpperCase()}.</span>
                </MaskedLine>
              </>
            ) : (
              <>
                <MaskedLine delay={0.15}>FROM</MaskedLine>
                <MaskedLine delay={0.28}>
                  <span className="bg-white text-tpeco-blue px-3 rounded-2xl inline-block animate-shiver" data-testid="hero-brrr">BRRR</span>
                </MaskedLine>
                <MaskedLine delay={0.41}>
                  TO <span className="bg-tpeco-pink text-white px-3 rounded-2xl inline-block" data-testid="hero-ahhh">
                    {"AHHH.".split("").map((ch, i) => (
                      <span key={i} className="animate-letter" style={{ animationDelay: `${i * 0.14}s` }}>{ch}</span>
                    ))}
                  </span>
                </MaskedLine>
              </>
            )}
          </h1>
          <div data-testid="hero-copy-glass">
          <MaskedLine delay={0.55}>
            <p className="mt-4 max-lg:mt-[calc(12svh+86vw-206px)] max-w-xl text-sm sm:text-base font-semibold text-white/90 max-lg:bg-tpeco-blue/75 max-lg:backdrop-blur-[3px] max-lg:rounded-2xl max-lg:p-3 max-lg:-ml-1 lg:w-fit lg:bg-tpeco-blue/60 lg:backdrop-blur-[4px] lg:rounded-[1.75rem] lg:px-5 lg:py-4 lg:-ml-2" data-testid="hero-subline">
              Hot water playing up{suburb ? ` in ${suburb.name}` : ""}? We replace, repair and install gas &amp; electric systems
              {suburb ? ` across ${suburb.name} and surrounds` : " across Perth"} — with
              <span className="text-white font-extrabold underline decoration-tpeco-pink decoration-4 underline-offset-4"> upfront swap-out prices listed online</span> and live booking availability.
              No waffle. No surprises. Just hot showers, fast.
            </p>
          </MaskedLine>
          <motion.div
            className="mt-6"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex flex-wrap items-start gap-3">
              <button onClick={onFindUnit} className="btn-brutal !flex-col !gap-0" data-testid="hero-find-unit-button">
                <span className="inline-flex items-center gap-2">
                  <Camera className="w-5 h-5" strokeWidth={2.5} /> SNAP A PHOTO FOR A PRICE
                </span>
                <span className="text-xs font-sans font-extrabold tracking-[0.2em] leading-tight" data-testid="hero-priced-instantly">
                  PRICED INSTANTLY!
                </span>
              </button>
              <a href="tel:0475776555" className="btn-brutal-light" data-testid="hero-call-button">
                <Phone className="w-5 h-5" strokeWidth={2.5} /> CALL 0475 776 555
              </a>
            </div>
            <p className="mt-2 text-xs sm:text-sm font-semibold text-white/90 max-w-md lg:w-fit lg:bg-tpeco-blue/60 lg:backdrop-blur-[4px] lg:rounded-2xl lg:px-4 lg:py-2.5 lg:-ml-1" data-testid="hero-cta-subdescription">
              Take a photo of your current unit — our AI reads it and matches you to an upfront swap-out price in seconds.
            </p>
            <div className="sm:hidden mt-3">
              <button onClick={onBook} className="btn-brutal-light !bg-tpeco-pink !border-white !text-white" data-testid="hero-beat-price-button-mobile">
                <BadgeDollarSign className="w-5 h-5" strokeWidth={2.5} /> FOUND A CHEAPER PRICE? WE WILL BEAT IT!
              </button>
              <p className="mt-2 text-xs font-semibold text-white/80 max-w-md">
                Show us a genuine written quote for the same unit and install — we'll beat it. Lock in your slot below.
              </p>
            </div>
          </motion.div>
          <motion.div
            className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-xs sm:text-sm font-bold uppercase tracking-wide lg:w-fit lg:bg-tpeco-blue/60 lg:backdrop-blur-[4px] lg:rounded-2xl lg:px-4 lg:py-2.5 lg:-ml-1"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.8 }}
            data-testid="hero-stats"
          >
            <span className="flex items-center gap-2"><BadgeCheck className="w-4 h-4" /> All prices listed online</span>
            <span className="flex items-center gap-2"><Clock className="w-4 h-4" /> Same / next day</span>
            <span className="flex items-center gap-2"><Star className="w-4 h-4 fill-white" /> 100+ five-star reviews</span>
          </motion.div>
          <motion.button
            onClick={onBook}
            className="mt-4 text-xs sm:text-sm font-bold uppercase tracking-widest underline decoration-tpeco-pink decoration-4 underline-offset-4 hover:text-tpeco-pink transition-colors lg:w-fit lg:bg-tpeco-blue/60 lg:backdrop-blur-[4px] lg:rounded-full lg:px-4 lg:py-2.5 lg:-ml-1 lg:block"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.15 }}
            data-testid="hero-book-link"
          >
            Book the next live slot →
          </motion.button>
          </div>
        </div>

        <motion.div
          className="max-lg:absolute max-lg:-right-[27%] max-lg:top-[12%] max-lg:w-[153%] max-lg:max-w-none max-lg:z-0 lg:absolute lg:inset-0 lg:z-0 lg:flex lg:items-center lg:justify-center"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="relative w-full lg:w-[min(98%,calc((100svh-6rem)*1.778))] lg:translate-x-[20%]">
            <HeroScene />
            <motion.a
              href="#reviews"
              onClick={(e) => { e.preventDefault(); document.getElementById("reviews")?.scrollIntoView({ behavior: "smooth" }); }}
              className="hidden sm:flex absolute -bottom-2 -left-1 lg:left-auto lg:right-10 lg:bottom-6 z-10 cursor-pointer flex-col items-center gap-1 overflow-hidden rounded-2xl border-[3px] border-white bg-white px-4 py-3 shadow-[0_5px_0_0_#0A0A0A] -rotate-3"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: [0, -7, 0], rotate: [-3, -1, -3] }}
              transition={{ opacity: { delay: 0.9, duration: 0.7 }, y: { repeat: Infinity, duration: 3, ease: "easeInOut", delay: 1.6 }, rotate: { repeat: Infinity, duration: 3, ease: "easeInOut", delay: 1.6 } }}
              whileHover={{ scale: 1.12, rotate: 0 }}
              data-testid="hero-reviews-badge"
            >
              <span className="pointer-events-none absolute inset-0 badge-shine" aria-hidden="true" />
              <span className="text-2xl font-bold tracking-tight leading-none" style={{ fontFamily: "Arial, Helvetica, sans-serif" }}>
                <span style={{ color: "#4285F4" }}>G</span>
                <span style={{ color: "#EA4335" }}>o</span>
                <span style={{ color: "#FBBC05" }}>o</span>
                <span style={{ color: "#4285F4" }}>g</span>
                <span style={{ color: "#34A853" }}>l</span>
                <span style={{ color: "#EA4335" }}>e</span>
              </span>
              <span className="flex gap-0.5">
                {[0, 1, 2, 3, 4].map((s) => (
                  <motion.span key={s} initial={{ scale: 0, rotate: -30 }}
                    animate={{ scale: [1, 1.3, 1], rotate: [0, 10, 0] }}
                    transition={{ repeat: Infinity, repeatDelay: 1.6, duration: 0.7, delay: 1.2 + s * 0.13 }}>
                    <Star className="w-4 h-4 fill-[#FBBC05] text-[#F59E0B] drop-shadow-[0_1px_0_rgba(0,0,0,0.25)]" />
                  </motion.span>
                ))}
              </span>
              <span className="font-display uppercase text-sm leading-none bg-gradient-to-r from-tpeco-blue to-tpeco-pink bg-clip-text text-transparent whitespace-nowrap">
                100+ 5 STAR REVIEWS
              </span>
              <span className="text-[10px] font-extrabold uppercase tracking-widest text-tpeco-blue/70">TAP TO READ THEM →</span>
            </motion.a>
            <motion.div
              className="hidden sm:flex absolute -top-1 lg:top-0 -right-1 z-10 flex-col items-end gap-2 max-w-sm text-right"
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1.05, type: "spring", stiffness: 200, damping: 14 }}
            >
              <button
                onClick={onBook}
                className="bg-white text-tpeco-pink border-[3px] border-tpeco-pink rounded-full px-4 py-1.5 font-display text-sm lg:text-base uppercase shadow-[0_5px_0_0_#0A0A0A] hover:translate-y-[2px] hover:shadow-[0_3px_0_0_#0A0A0A] transition-[transform,box-shadow] duration-150 inline-flex items-center gap-2"
                data-testid="hero-beat-price-button"
              >
                <BadgeDollarSign className="w-4 h-4" strokeWidth={2.5} /> FOUND A CHEAPER PRICE? WE WILL BEAT IT!
              </button>
              <p className="text-xs font-semibold text-white/95 bg-tpeco-blue/80 backdrop-blur-[2px] rounded-xl px-3 py-1.5" data-testid="hero-beat-price-subdescription">
                Show us a genuine written quote for the same unit and install — we'll beat it. Lock in your slot below.
              </p>
              <span
                className="bg-tpeco-pink text-white border-[3px] border-white rounded-full px-4 py-1.5 font-display text-sm sm:text-lg uppercase shadow-[0_5px_0_0_#0A0A0A] -rotate-3"
                data-testid="hero-price-tag"
              >
                UPFRONT PRICES. NO SURPRISES.
              </span>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
